import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import plur from "plur";
import { AgGrid, Button, Flex, Grid, HelpIndicator, Tag, Text, useCopy } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { HighlightText } from "~/pages/Reporting/components/HighlightText";
import { useAgGridDefaults } from "~/pages/UserAndRoles/utils/gridConfig";
import { SPACING } from "~/styles/variables";
import {
  getFieldDataTypeTagContent,
  fieldDataTypeTag,
} from "../../../shared/fieldDataTypeDisplay";
import { getFieldDataTypeLabel } from "../../../shared/fieldDataTypeLabel";
import { NoMatchesOverlay } from "../NoMatchesOverlay";
import {
  computeFieldGroupSpans,
  getNameCellSpanRole,
  groupFieldsByName,
  type FieldGroupSpans,
} from "./fieldGroupSpans";
import { getNameDisplayText, toTemplateTag } from "./fieldsMapper";
import { computeMergeFieldColumnWidth, MERGE_FIELD_MIN_WIDTH } from "./mergeFieldColumnWidth";
import * as styles from "./styles_vanilla.css";
import type { QueryField } from "./types";

/**
 * Single shared copy state for the merge-field column. Hoisted into a context
 * so we don't spin up an independent `useCopy` (and its associated timer) for
 * every row — only the most-recently-clicked row shows the "copied" check.
 */
type MergeFieldCopyContextValue = {
  copiedId: string | null;
  copyMergeField: (id: string, value: string) => void;
};

const MergeFieldCopyContext = createContext<MergeFieldCopyContextValue>({
  copiedId: null,
  copyMergeField: () => {},
});

/** Row spanning only applies in the default insertion-order view. When any
 *  column (including Name) is sorted, spans must collapse or AG Grid cannot
 *  reorder rows and sorted primary-field labels diverge from the sort key. */
const hasActiveColumnSort = (api: AgGrid.GridApi<QueryField>): boolean =>
  api.getColumnState().some((col) => col.sort != null);

/** Maps each name cell to one span role → one class (see styles_vanilla.css.ts).
 *  View mode adds a right edge on visible cells; edit mode uses Variant's divider. */
const getNameColumnCellClass =
  (
    fieldGroupSpans: FieldGroupSpans,
    isSorted: boolean,
    isViewMode: boolean,
  ): AgGrid.ColDef<QueryField>["cellClass"] =>
  (params) => {
    const role = isSorted
      ? "single"
      : getNameCellSpanRole(fieldGroupSpans, params.data?.id ?? "");

    if (role === "spannedOver") return styles.nameColumnSpannedOverCell;
    if (role === "spanHead") {
      return isViewMode ? styles.nameColumnSpanHeadCellView : styles.nameColumnSpanHeadCell;
    }
    return isViewMode
      ? [styles.nameColumnSingleCell, styles.nameColumnRightEdge]
      : styles.nameColumnSingleCell;
  };

/** `useAgGridDefaults` runs `sizeColumnsToFit` + `fitGridWidth` on resize,
 *  which can reorder/unpin left-pinned columns when the viewport is narrower
 *  than their combined minWidths. Restore explicit pin order instead. */
const applyPinnedLeftColumnState = (
  api: AgGrid.GridApi<QueryField>,
  isViewMode: boolean,
  mergeFieldWidth: number,
): void => {
  api.applyColumnState({
    state: [
      { colId: "name", pinned: "left" },
      // AG Grid keeps a column's existing width across column-def updates, so
      // the content-derived width has to be pushed through column state too.
      ...(isViewMode
        ? [{ colId: "mergeField", pinned: "left" as const, flex: null, width: mergeFieldWidth }]
        : []),
      { colId: "variant", pinned: "left" },
    ],
    applyOrder: true,
  });
};

type FieldsGridProps = {
  fields: QueryField[];
  searchValue?: string;
  /** When true the trailing settings/trash action column is hidden and row
   * clicks are inert. Defaults to true (view mode). */
  isViewMode?: boolean;
  onConfigureField?: (fieldId: string) => void;
  onDeleteField?: (fieldId: string) => void;
};

const fieldMatchesSearch = (
  field: QueryField,
  searchTerm: string,
  isViewMode: boolean,
): boolean => {
  const term = searchTerm.toLowerCase();
  const displayName = getNameDisplayText(field);
  const nameMatch =
    displayName.toLowerCase().includes(term) || field.name.toLowerCase().includes(term);
  if (!isViewMode) return nameMatch;
  return (
    nameMatch ||
    toTemplateTag(field.mergeField, field.isTransactionLevel).toLowerCase().includes(term)
  );
};

const FieldsNoRowsOverlay = () => {
  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap={SPACING.X_SMALL}
    >
      <Icon
        type="file-alt"
        size="xx-large"
        color={Colors.Neutral600}
        className={styles.noRowsOverlayIcon}
      />
      <Text variant="h4" align="center" className={styles.mutedHeading}>
        No data fields yet
      </Text>
      <Text variant="secondary-md" align="center">
        Add fields to get started
      </Text>
    </Flex>
  );
};

type GridContext = {
  searchValue: string;
};

const NameCellRenderer = (
  params: AgGrid.ICellRendererParams<QueryField, string, GridContext>,
) => {
  const { data } = params;
  if (!data) return null;
  const searchValue = params.context?.searchValue ?? "";
  return (
    <Flex
      justifyContent="space-between"
      alignItems="center"
      gap={SPACING.X_SMALL}
      className={styles.nameCell}
    >
      <Text lines={1} className={styles.nameText}>
        <HighlightText text={getNameDisplayText(data)} searchValue={searchValue} />
      </Text>
      {data.note ? <Tag label="Note" variant="neutral" title={data.note} /> : null}
    </Flex>
  );
};

const FieldTypeCellRenderer = (params: AgGrid.ICellRendererParams<QueryField, string>) => {
  const { data } = params;
  if (!data) return null;
  const tagContent = getFieldDataTypeTagContent(data.dataType);
  const dataTypeLabel = getFieldDataTypeLabel(data.dataType);
  return (
    <Flex
      alignItems="center"
      justifyContent="space-between"
      gap={SPACING.X_SMALL}
      className={styles.fieldTypeCell}
    >
      <Text lines={1}>{data.fieldType}</Text>
      {tagContent && dataTypeLabel ? (
        <HelpIndicator id={`field-type-tag-${data.id}`} placement="top" content={dataTypeLabel}>
          <Tag variant="neutral" className={fieldDataTypeTag}>
            {tagContent}
          </Tag>
        </HelpIndicator>
      ) : null}
    </Flex>
  );
};

const LevelCellRenderer = (params: AgGrid.ICellRendererParams<QueryField, string>) => {
  const { data } = params;
  if (!data) return null;
  const { level, levelFull, id } = data;
  const isTruncated = level !== levelFull && level !== "--";
  const text = <Text lines={1}>{level}</Text>;
  return (
    <Flex alignItems="center" className={styles.levelCell}>
      {isTruncated ? (
        <HelpIndicator id={`level-full-${id}`} placement="top" content={levelFull}>
          {text}
        </HelpIndicator>
      ) : (
        text
      )}
    </Flex>
  );
};

const DateRangeCellRenderer = (params: AgGrid.ICellRendererParams<QueryField, string>) => {
  const { data } = params;
  if (!data) return null;
  const offsetMonths = Math.abs(data.offsetMonths);
  const offsetLabel = `Offset by ${offsetMonths} ${plur("month", offsetMonths)}`;

  return (
    <Flex alignItems="center" justifyContent="space-between" className={styles.dateRangeCell}>
      <Text lines={1} className={styles.dateRangeText}>
        {data.dateRange}
      </Text>
      {data.hasDateOffset ? (
        <HelpIndicator
          id={`date-range-offset-${data.id}`}
          placement="top"
          content={offsetLabel}
        >
          <span className={styles.dateRangeOffsetIcon}>
            <Icon type="clock-rotate-left" size="small" color={Colors.Neutral700} />
          </span>
        </HelpIndicator>
      ) : null}
    </Flex>
  );
};

const MergeFieldCellRenderer = (
  params: AgGrid.ICellRendererParams<QueryField, string, GridContext>,
) => {
  const value = params.value ?? "";
  const fieldId = params.data?.id;
  const searchValue = params.context?.searchValue ?? "";
  const { copiedId, copyMergeField } = useContext(MergeFieldCopyContext);

  const handleCopy = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (fieldId) copyMergeField(fieldId, value);
    },
    [copyMergeField, fieldId, value],
  );

  const isCopied = fieldId !== undefined && copiedId === fieldId;

  return (
    <Flex
      alignItems="center"
      justifyContent="space-between"
      gap={SPACING.SMALL}
      className={styles.mergeFieldCell}
    >
      <Text variant="regular-sm" className={styles.mergeFieldText}>
        <HighlightText text={value} searchValue={searchValue} />
      </Text>
      <Button
        icon={isCopied ? "check" : "copy"}
        variant="muted"
        onClick={handleCopy}
        aria-label="Copy merge field"
        className={styles.copyButton}
      />
    </Flex>
  );
};

type ActionsCellProps = AgGrid.ICellRendererParams<QueryField, unknown> & {
  onConfigureField?: (fieldId: string) => void;
  onDeleteField?: (fieldId: string) => void;
};

const ActionsCellRenderer = ({ data, onConfigureField, onDeleteField }: ActionsCellProps) => {
  if (!data) return null;
  return (
    <Flex
      alignItems="center"
      justifyContent="flex-end"
      gap={SPACING.X_SMALL}
      className={styles.actionsCell}
    >
      <Button
        variant="muted"
        icon="cog"
        aria-label={`Configure ${data.name}`}
        data-testid={`configure-field-${data.id}`}
        onClick={(e) => {
          e.stopPropagation();
          onConfigureField?.(data.id);
        }}
      />
      <Button
        variant="muted"
        icon="trash"
        aria-label={`Remove ${data.name}`}
        data-testid={`remove-field-${data.id}`}
        onClick={(e) => {
          e.stopPropagation();
          onDeleteField?.(data.id);
        }}
      />
    </Flex>
  );
};

export const FieldsGrid = ({
  fields,
  searchValue = "",
  isViewMode = true,
  onConfigureField,
  onDeleteField,
}: FieldsGridProps) => {
  const { copy, attemptedCopy } = useCopy({ useTimeout: true });
  const [lastCopiedId, setLastCopiedId] = useState<string | null>(null);

  const copyMergeField = useCallback(
    (id: string, value: string) => {
      setLastCopiedId(id);
      void copy(value);
    },
    [copy],
  );

  const copyContextValue = useMemo<MergeFieldCopyContextValue>(
    () => ({
      copiedId: attemptedCopy ? lastCopiedId : null,
      copyMergeField,
    }),
    [attemptedCopy, lastCopiedId, copyMergeField],
  );

  const [isSorted, setIsSorted] = useState(false);
  const handleSortChanged = useCallback((event: AgGrid.SortChangedEvent<QueryField>) => {
    setIsSorted(hasActiveColumnSort(event.api));
  }, []);

  const rowData = useMemo<QueryField[]>(() => {
    const trimmed = searchValue.trim();
    const filtered = trimmed
      ? fields.filter((field) => fieldMatchesSearch(field, trimmed, isViewMode))
      : fields;
    return isSorted ? [...filtered] : groupFieldsByName(filtered);
  }, [fields, isSorted, searchValue, isViewMode]);

  /** Precompute row spans for grouped field names. Only the Name column
   *  spans — the head of a run of identical names gets a span > 1; rows
   *  beneath it are tracked in `spannedOver` and styled via `getNameColumnCellClass`. */

  /** When a sort is active, collapse all spans to 1 so AG Grid's row
   *  reordering doesn't cause spanned cells to visually cover wrong rows. */
  const fieldGroupSpans = useMemo(
    () => computeFieldGroupSpans(rowData, isSorted),
    [rowData, isSorted],
  );

  const gridContext = useMemo<GridContext>(() => ({ searchValue }), [searchValue]);

  /** Derived from the full field list, not the filtered rows, so the column
   *  doesn't jump around as the user types in the search box. */
  const mergeFieldWidth = useMemo(() => computeMergeFieldColumnWidth(fields), [fields]);

  const columnDefs = useMemo<AgGrid.ColDef<QueryField>[]>(() => {
    const nameColumnSpan = isSorted
      ? {}
      : {
          rowSpan: (params: AgGrid.RowSpanParams<QueryField>) =>
            fieldGroupSpans.name.get(params.data?.id ?? "") ?? 1,
        };

    const defs: AgGrid.ColDef<QueryField>[] = [
      {
        colId: "name",
        field: "name",
        headerName: "Name",
        sortable: true,
        minWidth: 200,
        pinned: "left",
        lockPinned: true,
        suppressMovable: true,
        suppressSizeToFit: true,
        headerClass: isViewMode ? styles.nameColumnHeader : undefined,
        cellClass: getNameColumnCellClass(fieldGroupSpans, isSorted, isViewMode),
        valueGetter: (params) => (params.data ? getNameDisplayText(params.data) : ""),
        cellRenderer: NameCellRenderer,
        ...nameColumnSpan,
      },
      ...(isViewMode
        ? [
            {
              colId: "mergeField",
              field: "mergeField",
              headerName: "Merge field",
              sortable: true,
              // Sized to the longest tag rather than flexed with the other
              // columns, so merge fields aren't ellipsized (up to the cap in
              // `mergeFieldColumnWidth.ts` that keeps the pinned region from
              // swallowing the viewport).
              flex: 0,
              width: mergeFieldWidth,
              minWidth: MERGE_FIELD_MIN_WIDTH,
              pinned: "left",
              lockPinned: true,
              suppressMovable: true,
              suppressSizeToFit: true,
              valueGetter: (params) =>
                params.data
                  ? toTemplateTag(params.data.mergeField, params.data.isTransactionLevel)
                  : "",
              cellRenderer: MergeFieldCellRenderer,
            } satisfies AgGrid.ColDef<QueryField>,
          ]
        : []),
      {
        colId: "variant",
        field: "variant",
        headerName: "Variant",
        sortable: true,
        minWidth: 115,
        maxWidth: 115,
        pinned: "left",
        lockPinned: true,
        suppressMovable: true,
        suppressSizeToFit: true,
        cellClass: styles.variantColumnDivider,
      },
      {
        field: "fieldType",
        headerName: "Field type",
        sortable: true,
        minWidth: 140,
        cellClass: styles.fieldTypeCell,
        cellRenderer: FieldTypeCellRenderer,
      },
      {
        field: "dateRange",
        headerName: "Date period",
        sortable: true,
        minWidth: 140,
        cellClass: styles.dateRangeCell,
        cellRenderer: DateRangeCellRenderer,
      },
      {
        field: "level",
        headerName: "Level",
        sortable: true,
        minWidth: 160,
        cellRenderer: LevelCellRenderer,
      },
      {
        field: "dataFormat",
        headerName: "Data format",
        sortable: true,
        minWidth: 140,
      },
      {
        field: "filterRules",
        headerName: "Filter rules",
        sortable: true,
        minWidth: 140,
      },
      {
        field: "suppressionRules",
        headerName: "Suppression rules",
        sortable: true,
        minWidth: 160,
      },
    ];

    if (!isViewMode) {
      defs.push({
        colId: "actions",
        headerName: "",
        pinned: "right",
        lockPinned: true,
        sortable: false,
        filter: false,
        suppressMovable: true,
        suppressSizeToFit: true,
        resizable: false,
        minWidth: 135,
        maxWidth: 135,
        cellClass: styles.pinnedActionsColumnCell,
        cellRenderer: ActionsCellRenderer,
        cellRendererParams: {
          onConfigureField,
          onDeleteField,
        },
      });
    }

    return defs;
  }, [fieldGroupSpans, isSorted, isViewMode, mergeFieldWidth, onConfigureField, onDeleteField]);

  const handleGridSizeChanged = useCallback(
    (event: AgGrid.GridSizeChangedEvent<QueryField>) => {
      applyPinnedLeftColumnState(event.api, isViewMode, mergeFieldWidth);
    },
    [isViewMode, mergeFieldWidth],
  );

  const { gridOptions } = useAgGridDefaults({
    overrides: {
      // Normal layout (not autoHeight) so the grid scrolls internally —
      // vertically past the container height and horizontally with a
      // functional native scrollbar. The container height lives in
      // `styles.gridContainer`.
      domLayout: "normal",
      rowHeight: 42,
      headerHeight: 48,
      suppressContextMenu: true,
      suppressCellFocus: true,
      pagination: false,
      sideBar: false,
      statusBar: undefined,
      autoSizeStrategy: undefined,
    },
  });

  /** AG Grid caches cell renderers by row id, so it won't re-render the
   *  highlighted cells just because we hand it a new `context`. Force a
   *  refresh on the search-aware columns whenever the search term
   *  changes — same pattern as `AddFieldPickerSlideout`. */
  /** Held in state, not a ref, so the effects below re-run once the grid
   *  exists. With a ref they bailed out on the renders before `onGridReady`
   *  and never retried, which left the merge field column stuck at the
   *  placeholder width computed while `fields` was still empty. */
  const [gridApi, setGridApi] = useState<AgGrid.GridApi<QueryField> | null>(null);
  const handleGridReady = useCallback((event: AgGrid.GridReadyEvent<QueryField>) => {
    setGridApi(event.api);
    setIsSorted(hasActiveColumnSort(event.api));
  }, []);

  useEffect(() => {
    if (!gridApi) return;
    applyPinnedLeftColumnState(gridApi, isViewMode, mergeFieldWidth);
  }, [gridApi, isViewMode, mergeFieldWidth]);

  useEffect(() => {
    if (!gridApi) return;
    gridApi.refreshCells({ columns: ["name"], force: true });
    gridApi.redrawRows();
  }, [gridApi, isSorted, fieldGroupSpans]);

  useEffect(() => {
    const highlightedCols = isViewMode ? ["name", "mergeField"] : ["name"];
    gridApi?.refreshCells({ columns: highlightedCols, force: true });
  }, [gridApi, searchValue, isViewMode]);

  const trimmedSearch = searchValue.trim();
  const noRowsOverlayComponent = useMemo(
    () => (fields.length > 0 && trimmedSearch ? NoMatchesOverlay : FieldsNoRowsOverlay),
    [fields.length, trimmedSearch],
  );

  return (
    <MergeFieldCopyContext.Provider value={copyContextValue}>
      <Grid<QueryField>
        rowData={rowData}
        getRowId={(params) => params.data.id}
        columnDefs={columnDefs}
        gridOptions={gridOptions}
        context={gridContext}
        onGridReady={handleGridReady}
        // Direct prop, not via `gridOptions`: ag-grid-react reads the
        // `gridOptions` object only at grid creation, so a handler nested in
        // it is frozen with its first-render closure — which would re-apply
        // the placeholder merge-field width on every window resize. Direct
        // props stay current across renders.
        onGridSizeChanged={handleGridSizeChanged}
        onSortChanged={handleSortChanged}
        noRowsOverlayComponent={noRowsOverlayComponent}
        // Direct prop, not via `gridOptions`: AG Grid 31 needs this on
        // the `AgGridReact` component for `colDef.rowSpan` to take
        // effect — it switches row positioning from CSS `transform` to
        // `top`, which is required for spanned cells to extend past
        // their own row's stacking context.
        suppressRowTransform
      />
    </MergeFieldCopyContext.Provider>
  );
};
